<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { useRoute } from 'vue-router';
    import DateRangePicker from '@shared/components/DateRangePicker.vue';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import StatisticsContactFilter from '@views/Lists/components/ContactFilter.vue';
    import type { SerializedContactFilterState } from '@views/Lists/components/ContactFilter.vue';
    import type { AdditionalField } from '@views/Lists/ListsTypes.d';
    import { getSmsStatisticsContactListHeadings } from '../sms-statistics-types';

    const props = defineProps<{
        dateRange: { start: string | null; end: string | null };
        showContactList: boolean;
        contactType?: string | null;
        availableFields: AdditionalField[];
    }>();

    const emit = defineEmits<{
        (e: 'update:dateRange', start: string | null, end: string | null): void;
        (e: 'update:contactFilter', filter: string): void;
        (e: 'update:contactFilterState', state: Partial<SerializedContactFilterState>): void;
    }>();

    const contactFilterRef = ref<InstanceType<typeof StatisticsContactFilter>>();

    defineExpose({ contactFilterRef });

    const route = useRoute();
    const contactListHeading = getSmsStatisticsContactListHeadings();

    const dateQuery = computed(() => {
        const q: Record<string, string> = {};
        if (route.query.start) q.start = route.query.start as string;
        if (route.query.end) q.end = route.query.end as string;
        if (route.query.contactFilter) q.contactFilter = route.query.contactFilter as string;
        if (route.query.contactFilterState) q.contactFilterState = route.query.contactFilterState as string;
        return q;
    });

    function onDateRangeChange(start: string, end: string) {
        emit('update:dateRange', start || null, end || null);
    }
</script>

<template>
    <nav class="navbar main-navbar" role="navigation">
        <div class="container-fluid">
            <p class="navbar-title">
                <router-link :to="{ name: 'TextMessages', query: { tab: 'sent' } }">{{ $Resources.SmsMenuItem }}</router-link>
                <span class="fw-400 c-light m-xs-l">/</span>
                <template v-if="props.showContactList">
                    <router-link class="m-xs-l" :to="{ name: 'TextMessageStatistics', params: { smsMessageId: route.params.smsMessageId }, query: dateQuery }">
                        {{ $Resources.IssueStatisticsBreadcrumb }}
                    </router-link>
                    <span class="fw-400 c-light m-xs-l">/</span>
                    {{ contactType ? (contactListHeading[contactType] ?? contactType) : '' }}
                </template>
                <template v-else>
                    <span class="m-xs-l">{{ $Resources.IssueStatisticsBreadcrumb }}</span>
                </template>
            </p>

            <div class="m-auto-l" v-show="!props.showContactList">
                <ul class="list-unstyled nav navbar-nav">
                    <li>
                        <DateRangePicker :dateFilter="{ start: dateRange.start, end: dateRange.end }" @onDateRangeChange="onDateRangeChange" />
                    </li>

                    <li>
                        <BSDropdownButton menuClass="dropdown-menu-right">
                            <template #toggle>
                                <span class="glyphicon glyphicon-filter m-xxs-r"></span>
                                <span class="navbar-label">{{ $Resources.FilterHeading }}</span>
                            </template>
                            <template #content>
                                <li class="dropdown-menu-content" :class="contactFilterRef?.filterCount && 'has-selected'">
                                    <StatisticsContactFilter
                                        ref="contactFilterRef"
                                        :availableFields="availableFields"
                                        :show-tags="true"
                                        @change="(f) => emit('update:contactFilter', f)"
                                        @update:filterState="(s) => emit('update:contactFilterState', s)" />
                                </li>
                            </template>
                        </BSDropdownButton>
                    </li>
                </ul>
            </div>
        </div>
    </nav>
</template>
